import { X } from "lucide-react";

// Pointer variant: single CTA, no queue position. The old trigger/queue variant
// was retired along with the nudge-queue engine (now advisor-driven).
type Props = {
  /** Optional discriminant kept for call-site clarity; only "pointer" exists. */
  variant?: "pointer";
  title: string;
  body: string;
  ctaLabel: string;
  onCta: () => void;
  /** Permanent dismissal (X button + acting on a suggestion). */
  onDismiss: () => void;
  /** Session-scoped snooze ("Later"). Optional. */
  onSnooze?: () => void;
  /** When false, the card drops its fixed bottom-right positioning so a parent
   *  can place it (used for the tour's mock nudge). Defaults to true. */
  floating?: boolean;
  /** Optional tour spotlight anchor applied to the card root. */
  "data-tour-target"?: string;
};

// Shared accent for pointer mode — uses the app's primary hue.
const POINTER_ACCENT = "var(--primary)";

// Card shell. `floating` adds the fixed bottom-right placement; without it the
// parent positions the card.
const CARD_BASE =
  "w-[348px] overflow-hidden rounded-xl border border-border bg-card";

export function SuggestionCard({
  title,
  body,
  ctaLabel,
  onCta,
  onDismiss,
  onSnooze,
  floating = true,
  "data-tour-target": dataTourTarget,
}: Props) {
  const accent = POINTER_ACCENT;

  return (
    <div
      role="dialog"
      aria-label={title}
      data-tour-target={dataTourTarget}
      className={floating ? `fixed bottom-5 right-5 z-40 ${CARD_BASE}` : CARD_BASE}
      style={{ boxShadow: "0 16px 40px -10px rgba(0,0,0,0.65)" }}
    >
      {/* Accent strip */}
      <div
        className="h-[3px]"
        style={{
          background: `linear-gradient(90deg, ${accent}, color-mix(in srgb, ${accent} 55%, transparent))`,
        }}
      />
      <div className="p-4">
        {/* Header row */}
        <div className="mb-2 flex items-center gap-2">
          <span
            className="flex size-[22px] items-center justify-center rounded"
            style={{
              background: `color-mix(in srgb, ${accent} 12%, transparent)`,
              color: accent,
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 99,
                background: accent,
                display: "inline-block",
              }}
            />
          </span>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: accent }}
          >
            SUGGESTION
          </span>
          <span className="flex-1" />
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </div>

        {/* Title */}
        <div
          className="mb-1.5 text-[15px] font-semibold leading-[1.3] text-foreground"
          style={{ letterSpacing: "-0.005em" }}
        >
          {title}
        </div>

        {/* Body */}
        <div className="mb-3.5 text-[12.5px] leading-[1.5] text-muted-foreground">
          {body}
        </div>

        {/* Single primary CTA */}
        <div className="mb-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={onCta}
            className="rounded-md px-3 py-1.5 text-[13px] font-medium"
            style={{ background: accent, color: "var(--background)" }}
          >
            {ctaLabel} →
          </button>
        </div>

        {/* Later link — session-scoped snooze, only when provided */}
        {onSnooze && (
          <div>
            <button
              type="button"
              onClick={onSnooze}
              className="bg-transparent p-1 text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
